/**
 * ErrorToast — In-game error/warning overlay.
 *
 * Shows the last 5 errors as colored toasts (bottom-left).
 * Red = error, Yellow = warning, Blue = info.
 * Click to dismiss. Auto-fades after timeout.
 *
 * Usage from anywhere:
 *   ErrorToast.error('Something broke')
 *   ErrorToast.warn('Dead event: combat:melee_hit')
 *   ErrorToast.info('Connected to server')
 */

import React, { useState, useEffect, useCallback } from 'react';

type ToastLevel = 'error' | 'warn' | 'info';

interface Toast {
  id: number;
  level: ToastLevel;
  message: string;
  timestamp: number;
}

const COLORS: Record<ToastLevel, { bg: string; border: string; text: string }> = {
  error: { bg: 'rgba(231,76,60,0.9)', border: '#c0392b', text: '#fff' },
  warn:  { bg: 'rgba(241,196,15,0.9)', border: '#f39c12', text: '#222' },
  info:  { bg: 'rgba(52,152,219,0.85)', border: '#2980b9', text: '#fff' },
};

const DURATIONS: Record<ToastLevel, number> = {
  error: 8000,
  warn: 5000,
  info: 3000,
};

const MAX_TOASTS = 5;

// ── Global API (callable from anywhere, no React context needed) ──

type ToastCallback = (level: ToastLevel, message: string) => void;
let _pushToast: ToastCallback | null = null;

export const ErrorToastAPI = {
  error: (msg: string) => _pushToast?.('error', msg),
  warn:  (msg: string) => _pushToast?.('warn', msg),
  info:  (_msg: string) => { /* info toasts in bottom-left disabled */ },
};

// ── React Component ──

let nextId = 0;

export const ErrorToastOverlay: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((level: ToastLevel, message: string) => {
    // Disable 'info' popups in bottom-left overlay to prevent cluttering combat/hotbar
    if (level === 'info') return;

    const id = ++nextId;
    setToasts(prev => {
      const next = [...prev, { id, level, message, timestamp: Date.now() }];
      return next.slice(-MAX_TOASTS);
    });

    // Auto-dismiss
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, DURATIONS[level]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Register global API (module-level + globalThis for main.tsx access)
  useEffect(() => {
    _pushToast = push;
    (globalThis as any).__errorToast = push;
    return () => {
      _pushToast = null;
      delete (globalThis as any).__errorToast;
    };
  }, [push]);

  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 80,
      left: 16,
      zIndex: 99999,
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      maxWidth: 420,
      pointerEvents: 'auto',
    }}>
      {toasts.map(t => {
        const c = COLORS[t.level] ?? COLORS.info;
        return (
          <div
            key={t.id}
            onClick={() => dismiss(t.id)}
            style={{
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: 6,
              padding: '8px 12px',
              color: c.text,
              fontSize: 13,
              fontFamily: 'monospace',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
              animation: 'fadeInUp 0.2s ease-out',
              wordBreak: 'break-word',
            }}
          >
            <span style={{ fontWeight: 'bold', marginRight: 6 }}>
              {t.level === 'error' ? 'ERR' : t.level === 'warn' ? 'WARN' : 'INFO'}
            </span>
            {t.message}
          </div>
        );
      })}
    </div>
  );
};
